import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Drawer, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { leaseInputSchema, type LeaseInputSchema } from '../../../lib/schemas';
import { useCreateLease, useUpdateLease } from '../../../queries/leases';
import { useLeaseFormLayout } from '../../../queries/lease-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { useAuthStore } from '../../../auth/auth-store';
import {
  LEASE_FORM_FIELDS,
  LEASE_FORM_FIELD_NAMES,
  LEASE_DEFAULT_LAYOUT,
  type LeaseFormContext,
} from '../../../lib/form-layout/lease-fields';
import { FormLayoutRenderer } from '../../../lib/form-layout/FormLayoutRenderer';
import { CrossReferenceSection } from '../../../lib/CrossReferenceSection';
import { ObligationAssignmentSection } from '../../../lib/ObligationAssignmentSection';
import { LeaseTractSection } from '../../../lib/LeaseTractSection';
import { ApiError } from '../../../lib/api-error';
import type { Lease } from '../../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  lease?: Lease | null;
  onSaved?: (l: Lease) => void;
}

const EMPTY: LeaseInputSchema = {
  lease_number: '',
  alt_lease_number: null,
  lease_type: null,
  lease_ownership: null,
  fed_rights: null,
  status: null,
  prospect: null,
  field: null,
  reviewer_user_id: null,
  lessor: null,
  lessee: null,
  county: null,
  state: null,
  township: null,
  range: null,
  section: null,
  block: null,
  lease_date: null,
  expire_date: null,
  term: null,
  book: null,
  page: null,
  entry: null,
  provided_description: null,
  description: null,
  gross_acres: null,
  net_acres: null,
  co_net_acres: null,
  orri: false,
  royalty: false,
  lease_on_minerals: false,
  depth_limitations: false,
  depth_limitations_remark: null,
  area_pugh: false,
  depth_pugh: false,
  remarks: null,
  reviewer_notes: null,
  change_remark: null,
};

function fromLease(l: Lease): LeaseInputSchema {
  const num = (v: string | number | null | undefined) =>
    v === null || v === undefined ? null : typeof v === 'string' ? Number(v) : v;
  return {
    lease_number: l.lease_number,
    alt_lease_number: l.alt_lease_number,
    lease_type: l.lease_type,
    lease_ownership: l.lease_ownership,
    fed_rights: l.fed_rights,
    status: l.status,
    prospect: l.prospect,
    field: l.field,
    reviewer_user_id: l.reviewer_user_id,
    lessor: l.lessor,
    lessee: l.lessee,
    county: l.county,
    state: l.state,
    township: l.township,
    range: l.range,
    section: l.section,
    block: l.block,
    lease_date: l.lease_date,
    expire_date: l.expire_date,
    term: l.term,
    book: l.book,
    page: l.page,
    entry: l.entry,
    provided_description: l.provided_description,
    description: l.description,
    gross_acres: num(l.gross_acres),
    net_acres: num(l.net_acres),
    co_net_acres: num(l.co_net_acres),
    orri: l.orri,
    royalty: l.royalty,
    lease_on_minerals: l.lease_on_minerals,
    depth_limitations: l.depth_limitations,
    depth_limitations_remark: l.depth_limitations_remark,
    area_pugh: l.area_pugh,
    depth_pugh: l.depth_pugh,
    remarks: l.remarks,
    reviewer_notes: l.reviewer_notes,
    change_remark: l.change_remark,
  };
}

const EMPTY_LOOKUPS = {
  lease_type: [],
  lease_status: [],
  lease_ownership: [],
  fed_rights: [],
  contract_type: [],
  flag_for_issues: [],
  well_status: [],
  well_type: [],
  unit_type: [],
};

export function LeaseFormDrawer({ opened, onClose, lease, onSaved }: Props) {
  const isEdit = !!lease;
  const createM = useCreateLease();
  const updateM = useUpdateLease();
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);

  const layoutQ = useLeaseFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? LEASE_DEFAULT_LAYOUT;

  // Fetched once at the drawer's top level (never inside the renderer's
  // per-field loop, which would break the Rules of Hooks) and threaded down
  // as the FormLayoutRenderer context — the 4 lookup-backed Selects in
  // lease-fields.tsx read `context.lookups`, mirroring
  // LeaseFormLayoutEditor's previewContext wiring.
  const { data: lookups } = useLandLookups();

  // Project members double as the reviewer_user_id dropdown's option list
  // AND the "users lookup" for resolving the display name behind
  // updated_by_user_id — /admin/users is superadmin-gated, but any project
  // member can see the membership roster.
  const { data: members } = useProjectMembers(projectId);
  const currentUser = useAuthStore((s) => s.user);
  // Server-enforced (spec §2.4): owner/superadmin may assign any project
  // member as reviewer; moderator/editor may only assign themselves. The UI
  // mirrors this to steer users away from an obvious 403, not to replace it.
  const canAssignAnyReviewer = role === 'owner' || currentUser?.system_role === 'superadmin';
  const formContext: LeaseFormContext = {
    lookups: lookups ?? EMPTY_LOOKUPS,
    members: (members ?? []).map((m) => ({ value: m.user_id, label: m.username })),
    canAssignAnyReviewer,
    currentUserId: currentUser?.id ?? '',
  };

  const updatedByName = lease?.updated_by_user_id
    ? (members?.find((m) => m.user_id === lease.updated_by_user_id)?.username ??
      lease.updated_by_user_id)
    : null;

  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<LeaseInputSchema>({
    resolver: zodResolver(leaseInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${lease?.id ?? 'new'}-${lease?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(lease ? fromLease(lease) : EMPTY);
  }

  async function onSubmit(values: LeaseInputSchema) {
    try {
      if (isEdit && lease) {
        const res = await updateM.mutateAsync({ id: lease.id, patch: values });
        notifications.show({ title: 'Lease saved', message: res.lease.lease_number, color: 'pine' });
        onSaved?.(res.lease);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({
          title: 'Lease created',
          message: res.lease.lease_number,
          color: 'pine',
        });
        onSaved?.(res.lease);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit lease' : 'New lease'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={LEASE_FORM_FIELDS}
            allNames={LEASE_FORM_FIELD_NAMES}
            context={formContext}
            isEdit={isEdit}
          />

          <CrossReferenceSection entity="lease" id={lease?.id ?? null} />

          <ObligationAssignmentSection host="lease" id={lease?.id ?? null} />

          <LeaseTractSection leaseId={lease?.id ?? null} />

          {isEdit && lease && (
            <>
              <Divider />
              <Text size="sm" c="dimmed">
                {`Last updated by ${updatedByName ?? '—'} on ${new Date(
                  lease.updated_at,
                ).toLocaleDateString()}`}
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create lease'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
